<template>
  <!-- aria-hidden: a pointer affordance, like the browser grip it replaces,
       which no keyboard can reach either. -->
  <v-icon
    class="textarea-resize-handle"
    icon="mdi-resize-bottom-right"
    size="24"
    aria-hidden="true"
    @pointerdown="onPointerDown"
    @mousedown.prevent
    @click.stop
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { resizedTextareaHeight } from '~/cynqueModules/TextareaResizeHandle/resizedTextareaHeight'

/**
 * A drag handle for a `v-textarea`, in place of the browser's own resize grip.
 * Rendered by EntityMaskForm in a textArea's `#append-inner` slot when the spec
 * sets `autoGrow: false`; the textarea itself is `no-resize`, so there is only
 * ever one grip.
 *
 * ⚠ WHY NOT THE BROWSER'S GRIP. It is tiny, and nothing makes it bigger
 * everywhere: `::-webkit-resizer` restyles it in Chrome and Safari only, sized
 * by the scrollbar, and Firefox offers no hook at all. An icon is sized and
 * themed like every other icon on the form.
 *
 * It sets the textarea's inline `height` -- exactly what dragging the native
 * grip does -- and the field grows with it. The height is read from the
 * COMPUTED style, not `offsetHeight`, so it means what `style.height` means
 * under whatever `box-sizing` the textarea has.
 */
export default defineComponent({
  name: 'TextareaResizeHandle',
  data() {
    return {
      // The textarea's height before any drag: a drag cannot take it below.
      // Taken at the first press rather than at mount, when a dialog still
      // mid-transition can report 0.
      minHeight: null as number | null,
    }
  },
  methods: {
    onPointerDown(event: PointerEvent) {
      const handle = event.currentTarget as HTMLElement
      const textarea = handle
        .closest('.v-field')
        ?.querySelector('textarea') as HTMLTextAreaElement | null
      if (!textarea) {
        return
      }
      // Keep the press from focusing the field or selecting text.
      event.preventDefault()
      event.stopPropagation()

      const startHeight = parseFloat(getComputedStyle(textarea).height)
      if (this.minHeight === null) {
        this.minHeight = startHeight
      }
      const minHeight = this.minHeight
      const startY = event.clientY

      // Captured, so the drag keeps tracking when the pointer leaves the
      // handle -- which it does at once, being 24px wide.
      handle.setPointerCapture(event.pointerId)
      const onMove = (e: PointerEvent) => {
        const height = resizedTextareaHeight({
          startHeight,
          startY,
          currentY: e.clientY,
          minHeight,
        })
        textarea.style.height = `${height}px`
      }
      const onEnd = () => {
        handle.removeEventListener('pointermove', onMove)
        handle.removeEventListener('pointerup', onEnd)
        handle.removeEventListener('pointercancel', onEnd)
      }
      handle.addEventListener('pointermove', onMove)
      handle.addEventListener('pointerup', onEnd)
      handle.addEventListener('pointercancel', onEnd)
    },
  },
})
</script>

<style scoped>
/* In the field's bottom-right corner, where the browser grip was. `.v-field` is
   the positioned ancestor (position: relative in VField.css), so this sits
   inside the outline whatever the append-inner slot's own padding. */
.textarea-resize-handle {
  position: absolute;
  right: 2px;
  bottom: 2px;
  cursor: ns-resize;
  /* Without it a touch drag scrolls the page instead of resizing. */
  touch-action: none;
}
</style>
